import React, { useMemo } from 'react';

import InsulationPickerFilters from './InsulationPickerFilters.js';
import InsulationPickerActiveFilters from './InsulationPickerActiveFilters.js';
import InsulationPickerDemand from './InsulationPickerDemand.js';
import InsulationPickerDigest from './InsulationPickerDigest.js';
import InsulationPickerSearch from './InsulationPickerSearch.js';
import InsulationPickerTable from './InsulationPickerTable.js';
import {
  MEASURE_LABELS,
  buildActiveFilters,
  buildApplicationDigest,
  buildApplicationFilterOptions,
  buildMeasureBounds,
  buildPickerRows,
  buildThicknessOptions,
  cheapestForDemand,
  cheapestRow,
  filterPickerRows,
  sortPickerRows,
} from './insulationPickerView.js';

import './insulationPicker.css';

/**
 * Умный подбор утеплителя — отдельный инструмент над «Товарами», а не второй
 * расчёт. Его дело одно: показать, что дешевле за куб под заданные условия, и
 * отдать выбранное в строку заказа.
 *
 * Каталог берётся из `options` — того же справочника, из которого работает
 * расчёт. Своего запроса у подбора нет намеренно: любой второй источник цен
 * означал бы, что таблица показывает одно, а в заказ уходит другое.
 *
 * Своего состояния компонент не держит: фильтры и количества живут в «Товарах»
 * (useInsulationPickerState), иначе «Скрыть подбор» стирал бы набранный отбор.
 * Здесь остаётся только счёт по готовому состоянию и разметка.
 */
const InsulationPicker = ({ paymentMethod = 'cash', options = {}, state, onSelect, onClose }) => {
  const {
    kind, applicationKeys, thicknesses, range, search, digestOpen, sort, sortColumn, demand,
    changeKind, resetFilters, toggleApplication, toggleThickness, changeRange, changeSearch,
    toggleDigest, changeSort, changeDemandValue, changeDemandUnit,
  } = state;

  // Форма оплаты меняет и цену, и выбранного справочником поставщика, поэтому
  // строки пересобираются вместе с ней.
  const rows = useMemo(
    () => buildPickerRows({ options, paymentMethod }),
    [options, paymentMethod]
  );

  const rowsOfKind = useMemo(() => rows.filter((row) => row.kind === kind), [rows, kind]);

  // Пункты фильтров считаются по виду материала, но без учёта самих фильтров:
  // иначе выбор одного применения убирал бы из списка все остальные.
  const applicationOptions = useMemo(() => buildApplicationFilterOptions(rowsOfKind), [rowsOfKind]);
  const thicknessOptions = useMemo(() => buildThicknessOptions(rowsOfKind), [rowsOfKind]);
  const measureBounds = useMemo(() => buildMeasureBounds(rowsOfKind), [rowsOfKind]);

  const filtered = useMemo(
    () => filterPickerRows({ rows, kind, applicationKeys, thicknesses, range, search }),
    [rows, kind, applicationKeys, thicknesses, range, search]
  );

  // Что сейчас отбирает — одной лентой над таблицей. Без неё фильтр, уехавший
  // из прокрутки списка применений, молча обнуляет выдачу.
  const activeFilters = useMemo(
    () => buildActiveFilters({
      applicationKeys, applicationOptions, thicknesses, range, search,
      measure: MEASURE_LABELS[kind], kind,
    }),
    [applicationKeys, applicationOptions, thicknesses, range, search, kind]
  );

  const removeFilter = (filter) => {
    if (filter.type === 'application') toggleApplication(filter.key);
    else if (filter.type === 'thickness') toggleThickness(filter.key);
    else if (filter.type === 'search') changeSearch('');
    else {
      changeRange('from', '');
      changeRange('to', '');
    }
  };

  // «Итого» и «Сумма» считаются от потребности, поэтому она входит в порядок
  // строк наравне с направлением и столбцом.
  const sorted = useMemo(
    () => sortPickerRows(filtered, sort, sortColumn, demand),
    [filtered, sort, sortColumn, demand]
  );
  // Пока потребность не задана, дешевле — то, у чего ниже цена за куб. Как
  // только она задана, дешевле — то, что меньше стоит под неё целиком: кратность
  // округляет каждую строку вверх по-своему, и позиция с дешёвым кубом, но
  // крупной упаковкой на 1000 м² выходит дороже соседа с упаковкой помельче.
  const best = useMemo(
    () => (demand.value ? cheapestForDemand(filtered, demand) : cheapestRow(filtered)),
    [filtered, demand]
  );

  // Пока применение не выбрано, над таблицей стоит ответ на первый пункт
  // задания. Считается он по уже отфильтрованным строкам, иначе сводка
  // противоречила бы таблице под собой.
  const digest = useMemo(
    () => (applicationKeys.length === 0 ? buildApplicationDigest({ rows: filtered }) : null),
    [filtered, applicationKeys]
  );

  return (
    <section className="ins-pick" aria-label="Умный подбор утеплителя">
      <header className="ins-pick__head">
        <h3 className="ins-pick__title">Умный подбор утеплителя</h3>
        <button type="button" className="ins-pick__close" onClick={onClose} aria-label="Закрыть подбор">
          ×
        </button>
      </header>

      <InsulationPickerFilters
        kind={kind}
        onKindChange={changeKind}
        measure={MEASURE_LABELS[kind]}
        applicationOptions={applicationOptions}
        applicationKeys={applicationKeys}
        onToggleApplication={toggleApplication}
        thicknessOptions={thicknessOptions}
        thicknesses={thicknesses}
        onToggleThickness={toggleThickness}
        range={range}
        onRangeChange={changeRange}
        measureBounds={measureBounds}
      />

      {rows.length === 0 ? (
        <p className="ins-pick__note">Справочник ещё загружается либо утеплителя в нём нет.</p>
      ) : (
        <>
          <p className="ins-pick__note">
            Цены и поставщики — те же, что подставит расчёт: справочник уже выбрал по каждой
            позиции самое дешёвое предложение региона.
          </p>

          {/* Порядок блоков задан на демонстрации: сначала сводка «с чего
              начинать», потом потребность, потом поиск. Он же и есть порядок
              работы — от «что вообще есть» к «сколько надо» и «где это». */}
          {digest && (
            <InsulationPickerDigest items={digest} isOpen={digestOpen} onToggle={toggleDigest} />
          )}

          <InsulationPickerDemand
            demand={demand}
            onChangeValue={changeDemandValue}
            onChangeUnit={changeDemandUnit}
          />

          <InsulationPickerSearch search={search} onSearchChange={changeSearch} />

          <InsulationPickerActiveFilters
            filters={activeFilters}
            onRemove={removeFilter}
            onReset={resetFilters}
          />

          <InsulationPickerTable
            rows={sorted}
            best={best}
            measure={MEASURE_LABELS[kind]}
            sort={sort}
            sortColumn={sortColumn}
            onSort={changeSort}
            demand={demand}
            onSelect={onSelect}
            activeFilterCount={activeFilters.filter((filter) => filter.removable !== false).length}
          />
        </>
      )}
    </section>
  );
};

export default InsulationPicker;
